import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Camera, Wifi, WifiOff, Monitor, Grid, Search, RefreshCw, Maximize2, Play, Pause, Zap, Settings, AlertTriangle } from 'lucide-react';
import { cctvService } from '../services/cctvService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../services/api';
import CameraImage from '../components/CameraImage';

const CCTV = () => {
  const [selectedRecorder, setSelectedRecorder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [gridSize, setGridSize] = useState(4); // 2x2, 3x3, 4x4
  const [playingStreams, setPlayingStreams] = useState(new Set());
  const [editingChannel, setEditingChannel] = useState(null); // { cameraIndex, channel }
  const queryClient = useQueryClient();
  const token = localStorage.getItem('token');
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://farm.my-it.pl/api';

  // Pobierz wszystkie urządzenia - używamy dedykowanego endpointu dla CCTV
  const { data: devicesData, isLoading, refetch, error } = useQuery(
    ['cctv-recorders'],
    async () => {
      try {
        // Użyj dedykowanego endpointu dla rejestratorów CCTV
        const response = await cctvService.getRecorders();
        console.log('📹 CCTV - Pełna odpowiedź API:', response);
        console.log('📹 CCTV - devicesData:', response?.devices);
        console.log('📹 CCTV - Liczba rejestratorów:', response?.count);
        
        if (response?.devices) {
          response.devices.forEach(r => {
            console.log('📹 Rejestrator:', r.name, 'Type:', r.type, 'Status:', r.status);
          });
        }
        
        // Zwróć w formacie zgodnym z devicesData
        return {
          devices: response?.devices || [],
          count: response?.count || 0
        };
      } catch (err) {
        console.error('📹 CCTV - Błąd pobierania urządzeń:', err);
        throw err;
      }
    },
    {
      staleTime: 30000, // 30 sekund
      cacheTime: 5 * 60 * 1000, // 5 minut
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  // Filtruj tylko rejestratory kamer
  const allCameraRecorders = React.useMemo(() => {
    if (!devicesData?.devices) {
      console.log('📹 CCTV - Brak danych devicesData lub devices');
      return [];
    }
    
    const devices = Array.isArray(devicesData.devices) ? devicesData.devices : [];
    console.log('📹 CCTV - Liczba wszystkich urządzeń:', devices.length);
    
    const filtered = devices.filter(device => {
      const isCameraRecorder = device.type === 'camera_recorder';
      if (isCameraRecorder) {
        console.log('📹 Znaleziono rejestrator:', device.name, 'Type:', device.type, 'Status:', device.status);
      }
      return isCameraRecorder;
    });
    
    console.log('📹 CCTV - Liczba rejestratorów kamer:', filtered.length);
    return filtered;
  }, [devicesData]);

  console.log('📹 CCTV - allCameraRecorders:', allCameraRecorders);

  // Filtruj rejestratory na podstawie wyszukiwania
  const cameraRecorders = React.useMemo(() => {
    return allCameraRecorders.filter(recorder =>
      recorder.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allCameraRecorders, searchTerm]);

  // Pobierz kamery dla wybranego rejestratora
  const {
    data: camerasData,
    isLoading: camerasLoading,
    error: camerasError,
    refetch: refetchCameras
  } = useQuery(
    ['cameras', selectedRecorder?._id],
    () => cctvService.getCameras(selectedRecorder._id, true),
    {
      enabled: !!selectedRecorder,
      retry: false,
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Błąd pobierania kamer:', error);
      }
    }
  );

  const camerasErrorMessage =
    camerasError?.response?.data?.message || (camerasError ? 'Nie udało się połączyć z rejestratorem' : null);

  // Automatyczne wykrywanie typu urządzenia przy pierwszym wyborze
  useEffect(() => {
    if (selectedRecorder && !selectedRecorder.metadata?.vendor) {
      // Automatycznie wykryj typ urządzenia
      cctvService.detectDeviceType(selectedRecorder._id)
        .then((result) => {
          console.log('✅ Wykryto typ urządzenia:', result);
          toast.success(`Wykryto: ${result.detection.vendor} (${result.detection.protocol})`);
          // Invaliduj cache aby pobrać zaktualizowane dane
          queryClient.invalidateQueries(['cctv-recorders']);
          refetch(); // Odśwież listę urządzeń
          refetchCameras(); // Odśwież kamery
        })
        .catch((error) => {
          console.error('Błąd wykrywania typu urządzenia:', error);
          toast.error('Nie udało się wykryć typu urządzenia. Używam domyślnych ustawień.');
        });
    }
  }, [selectedRecorder, refetch, refetchCameras, queryClient]);

  const toggleStream = (cameraId) => {
    const newPlayingStreams = new Set(playingStreams);
    if (newPlayingStreams.has(cameraId)) {
      newPlayingStreams.delete(cameraId);
    } else {
      newPlayingStreams.add(cameraId);
    }
    setPlayingStreams(newPlayingStreams);
  };

  const handleRefresh = () => {
    // Invaliduj cache i wymuś ponowne pobranie
    queryClient.invalidateQueries(['cctv-recorders']);
    refetch();
    if (selectedRecorder) refetchCameras();
  };

  const handleRecorderSelect = (recorder) => {
    console.log('📹 Wybrano rejestrator:', recorder);
    setSelectedRecorder(recorder);
    setPlayingStreams(new Set()); // Resetuj odtwarzane strumienie
  };

  // Użyj rzeczywistych kamer z API
  const cameras = camerasData?.cameras || [];
  const onlineCameras = cameras.filter((camera) => camera.status === 'online').length;

  const getGridClass = () => {
    switch (gridSize) {
      case 2: return 'grid-cols-2';
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-4';
      default: return 'grid-cols-4';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-500">Ładowanie rejestratorów...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-red-600 mb-4">Błąd podczas ładowania rejestratorów</p>
        <button onClick={handleRefresh} className="btn-primary">
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Odśwież
          </button>
          <div className="flex items-center space-x-2 border rounded-lg p-1">
            <button
              onClick={() => setGridSize(2)}
              className={`p-2 rounded ${gridSize === 2 ? 'bg-primary-600 text-white' : 'hover:bg-gray-100'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setGridSize(3)}
              className={`p-2 rounded ${gridSize === 3 ? 'bg-primary-600 text-white' : 'hover:bg-gray-100'}`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setGridSize(4)}
              className={`p-2 rounded ${gridSize === 4 ? 'bg-primary-600 text-white' : 'hover:bg-gray-100'}`}
            >
              <Grid className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Lista rejestratorów */}
        <div className="col-span-12 lg:col-span-3">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Rejestratory</h3>
            </div>
            <div className="card-body">
              {/* Wyszukiwarka */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Szukaj rejestratora..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10"
                  />
                </div>
              </div>

              {/* Lista rejestratorów */}
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {cameraRecorders.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">
                    Brak dostępnych rejestratorów
                  </p>
                ) : (
                  cameraRecorders.map((recorder) => (
                    <div
                      key={recorder._id}
                      onClick={() => handleRecorderSelect(recorder)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedRecorder?._id === recorder._id
                          ? 'bg-primary-50 border-2 border-primary-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 min-w-0">
                          <Camera className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {recorder.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {recorder.ipAddress}:{recorder.port}
                            </p>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          {recorder.status === 'online' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Wifi className="h-3 w-3 mr-1" />
                              Online
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <WifiOff className="h-3 w-3 mr-1" />
                              Offline
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Główny obszar - Podgląd kamer */}
        <div className="col-span-12 lg:col-span-9">
          {selectedRecorder ? (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {selectedRecorder.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      IP: {selectedRecorder.ipAddress}:{selectedRecorder.port}
                      {cameras.length > 0 && (
                        <span className="ml-2">
                          &middot; {onlineCameras} z {cameras.length} kamer zgłasza połączenie
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {selectedRecorder.status === 'online' ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <Wifi className="h-4 w-4 mr-1" />
                        Online
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        <WifiOff className="h-4 w-4 mr-1" />
                        Offline
                      </span>
                    )}
                    {!selectedRecorder.metadata?.vendor && (
                      <button
                        onClick={() => {
                          cctvService.detectDeviceType(selectedRecorder._id)
                            .then((result) => {
                              toast.success(`Wykryto: ${result.detection.vendor} (${result.detection.protocol})`);
                              queryClient.invalidateQueries(['cctv-recorders']);
                              refetch();
                              refetchCameras();
                            })
                            .catch((error) => {
                              console.error('Błąd wykrywania typu urządzenia:', error);
                              toast.error('Błąd wykrywania typu urządzenia');
                            });
                        }}
                        className="btn-primary flex items-center"
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Wykryj typ urządzenia
                      </button>
                    )}
                    {selectedRecorder.metadata?.vendor === 'generic' && (
                      <button
                        onClick={() => {
                          if (window.confirm('Czy na pewno chcesz ustawić ten rejestrator jako Hikvision?')) {
                            api.put(`/cctv/${selectedRecorder._id}/settings`, {
                              vendor: 'hikvision',
                              protocol: 'hikvision_isapi',
                              rtspPort: 5544
                            })
                              .then(() => {
                                toast.success('Rejestrator ustawiony jako Hikvision!');
                                queryClient.invalidateQueries(['cctv-recorders']);
                                refetch();
                                refetchCameras();
                              })
                              .catch((error) => {
                                console.error('Błąd aktualizacji ustawień:', error);
                                toast.error('Błąd aktualizacji ustawień');
                              });
                          }
                        }}
                        className="btn-secondary flex items-center text-sm"
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Ustaw jako Hikvision
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-body">
                {camerasLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner size="lg" />
                    <span className="ml-3 text-gray-500">Ładowanie kamer...</span>
                  </div>
                ) : camerasErrorMessage ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-gray-700 font-medium">Nie można pobrać listy kamer</p>
                    <p className="text-sm text-gray-500 mt-2 max-w-lg mx-auto">{camerasErrorMessage}</p>
                    <button onClick={() => refetchCameras()} className="mt-4 btn-secondary flex items-center mx-auto">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Spróbuj ponownie
                    </button>
                  </div>
                ) : cameras.length === 0 ? (
                  <div className="text-center py-12">
                    <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Brak dostępnych kamer</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {selectedRecorder.metadata?.vendor 
                        ? `Typ: ${selectedRecorder.metadata.vendor} - Sprawdź połączenie z rejestratorem`
                        : 'Trwa wykrywanie typu urządzenia...'}
                    </p>
                    {!selectedRecorder.metadata?.vendor && (
                      <button
                        onClick={() => {
                          cctvService.detectDeviceType(selectedRecorder._id)
                            .then(() => {
                              toast.success('Typ urządzenia wykryty!');
                              refetch();
                              refetchCameras();
                            })
                            .catch(() => toast.error('Błąd wykrywania typu urządzenia'));
                        }}
                        className="mt-4 btn-primary flex items-center mx-auto"
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Wykryj typ urządzenia
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={`grid ${getGridClass()} gap-4`}>
                    {cameras.map((camera, index) => (
                      <div
                        key={camera.id}
                        className="relative bg-black rounded-lg overflow-hidden aspect-video group"
                      >
                        {/* Obraz/Snapshot */}
                        <CameraImage
                          camera={camera}
                          cameraIndex={index}
                          isPlaying={playingStreams.has(camera.id)}
                          onError={(error) => {
                            console.error('Błąd ładowania obrazu kamery:', error);
                          }}
                        />

                        {/* Overlay z nazwą kamery */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                          <p className="text-white text-sm font-medium">{camera.name}</p>
                          <p className="text-white text-xs opacity-75">
                            Kanał: {camera.channel ?? 'brak mapowania'}
                            {camera.ipAddress && ` · ${camera.ipAddress}`}
                          </p>
                        </div>

                        {/* Kontrolki */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setEditingChannel({
                                cameraIndex: camera.proxyId,
                                channel: camera.channel,
                                cameraName: camera.name
                              })}
                              className="bg-black/50 hover:bg-black/70 text-white p-2 rounded"
                              title="Ustawienia kanału"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                            {camera.enabled !== false && (
                              <>
                                <button
                                  onClick={() => toggleStream(camera.id)}
                                  className="bg-black/50 hover:bg-black/70 text-white p-2 rounded"
                                  title={playingStreams.has(camera.id) ? 'Zatrzymaj' : 'Odtwórz'}
                                >
                                  {playingStreams.has(camera.id) ? (
                                    <Pause className="h-4 w-4" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => {
                                    const proxySnapshotUrl = camera.streamUrls?.proxySnapshotUrl;
                                    const fullUrl = proxySnapshotUrl.startsWith('http')
                                      ? proxySnapshotUrl
                                      : `${API_BASE_URL}${proxySnapshotUrl}`;
                                    const separator = fullUrl.includes('?') ? '&' : '?';
                                    const urlWithToken = `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
                                    const fullscreenUrl = `/camera-fullscreen.html?url=${encodeURIComponent(urlWithToken)}&name=${encodeURIComponent(camera.name)}&interval=2000`;
                                    window.open(fullscreenUrl, '_blank', 'width=1280,height=720');
                                  }}
                                  className="bg-black/50 hover:bg-black/70 text-white p-2 rounded"
                                  title="Pełny ekran"
                                >
                                  <Maximize2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Status odtwarzania */}
                        {playingStreams.has(camera.id) && (
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center px-2 py-1 rounded bg-green-500 text-white text-xs">
                              <Monitor className="h-3 w-3 mr-1" />
                              LIVE
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <div className="text-center py-12">
                  <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Wybierz rejestrator
                  </h3>
                  <p className="text-gray-500">
                    Wybierz rejestrator z listy po lewej stronie, aby zobaczyć podgląd kamer
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal do edycji kanału */}
      {editingChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Ustaw kanał dla kamery</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Kamera: {editingChannel.cameraName}</p>
              <p className="text-sm text-gray-600 mb-4">Aktualny kanał: {editingChannel.channel}</p>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nowy kanał:
              </label>
              <input
                type="number"
                value={editingChannel.channel}
                onChange={(e) => setEditingChannel({ ...editingChannel, channel: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="np. 101, 201, 1201..."
                min="1"
              />
              <p className="text-xs text-gray-500 mt-2">
                Hikvision numeruje kanały jako numer kamery razy 100 plus numer strumienia:
                kamera 1 to 101, kamera 2 to 201, a kamera 12 to 1201. Numery są pobierane
                automatycznie z rejestratora - zmieniaj je tylko wtedy, gdy podgląd pokazuje
                obraz z innej kamery.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={async () => {
                  try {
                    await api.put(`/cctv/${selectedRecorder._id}/camera/${editingChannel.cameraIndex}/channel`, {
                      channel: editingChannel.channel
                    });
                    toast.success('Kanał został zapisany');
                    setEditingChannel(null);
                    refetchCameras();
                  } catch (error) {
                    console.error('Błąd zapisywania kanału:', error);
                    toast.error('Błąd zapisywania kanału');
                  }
                }}
                className="flex-1 btn-primary"
              >
                Zapisz
              </button>
              <button
                onClick={async () => {
                  try {
                    await api.delete(`/cctv/${selectedRecorder._id}/camera/${editingChannel.cameraIndex}/channel`);
                    toast.success('Przywrócono automatyczny kanał');
                    setEditingChannel(null);
                    refetchCameras();
                  } catch (error) {
                    console.error('Błąd usuwania kanału:', error);
                    toast.error('Błąd usuwania kanału');
                  }
                }}
                className="flex-1 btn-secondary"
              >
                Przywróć automatyczny
              </button>
              <button
                onClick={() => setEditingChannel(null)}
                className="flex-1 btn-secondary"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CCTV;
